import { useEffect, useRef, useState } from "react";
import PropertyBadge from "./PropertyBadge";
import ServiceSection from "./ServiceSection";


const SelectedPreview = ({ selected }) => {
  if (!selected) return null;
  return (
    <div style={{
      background: "linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)",
      border: "1.5px solid #bfdbfe",
      borderRadius: 10,
      padding: "10px 14px",
      marginBottom: 14,
      animation: "popIn 0.2s ease",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        ✓ Selected Step
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{selected.char_desc}</div>
      <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
        {selected.can_read   && <PropertyBadge type="read"   expanded />}
        {selected.can_write  && <PropertyBadge type="write"  expanded />}
        {selected.can_notify && <PropertyBadge type="notify" expanded />}
      </div>
    </div>
  );
};

// ── Service Step Modal ────────────────────────────────────────────────────────
const ServiceStepModal = ({ services , onSelect, onClose }) => {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);
 
  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setVisible(true));
    // Focus search
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);
 
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 200);
  };
 
  const handleAdd = () => {
    if (!selected) return;
    setVisible(false);
    setTimeout(() => onSelect?.(selected), 200);
  };
 
  // Filter by search
  const query = search.toLowerCase().trim();
  const filtered = (services || []).map(svc => ({
    ...svc,
    characteristics: (svc.characteristics || []).filter(c =>
      !query ||
      c.description.toLowerCase().includes(query) ||
      svc.description.toLowerCase().includes(query) ||
      c.properties?.some(p => p.includes(query))
    ),
  })).filter(svc => svc.characteristics.length > 0);
 
  const totalChars = filtered.reduce((n, s) => n + s.characteristics.length, 0);
 
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
 
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes backdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes popIn {
          0%   { opacity: 0; transform: scale(0.95); }
          60%  { transform: scale(1.02); }
          100% { opacity: 1; transform: scale(1); }
        }
 
        .ssm-overlay {
          animation: backdropIn 0.2s ease;
        }
        .ssm-card {
          animation: modalIn 0.25s cubic-bezier(0.34, 1.2, 0.64, 1);
          font-family: 'DM Sans', sans-serif;
        }
        .ssm-search:focus {
          outline: none;
          border-color: #1d4ed8 !important;
          box-shadow: 0 0 0 3px rgba(29,78,216,0.12);
        }
        .ssm-add:hover:not(:disabled) {
          background: linear-gradient(135deg, #1e40af, #1d4ed8) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(29,78,216,0.4) !important;
        }
        .ssm-add:active:not(:disabled) {
          transform: translateY(0);
        }
        .ssm-cancel:hover {
          background: #f1f5f9 !important;
          color: #334155 !important;
        }
        .ssm-close:hover {
          background: #fee2e2 !important;
          border-color: #fca5a5 !important;
          color: #dc2626 !important;
        }
      `}</style>
 
      {/* Backdrop */}
      <div
        className="ssm-overlay"
        onClick={handleClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 400,
          backdropFilter: "blur(6px)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      >
        {/* Card */}
        <div
          className="ssm-card"
          onClick={e => e.stopPropagation()}
          style={{
            background: "white",
            borderRadius: 18,
            width: 460,
            maxHeight: "82vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 30px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)",
            overflow: "hidden",
            opacity: visible ? 1 : 0,
            transform: visible ? "scale(1)" : "scale(0.95)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
          }}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div style={{
            padding: "20px 20px 0",
            background: "linear-gradient(180deg, #f8fafc 0%, white 100%)",
            borderBottom: "1px solid #f1f5f9",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: "linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(29,78,216,0.35)",
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} style={{ width: 18, height: 18 }}>
                    <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", letterSpacing: -0.3 }}>
                    Add Service Step
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {totalChars} characteristic{totalChars !== 1 ? "s" : ""} available
                  </div>
                </div>
              </div>
              <button
                className="ssm-close"
                onClick={handleClose}
                style={{
                  width: 32, height: 32, borderRadius: 9,
                  border: "1px solid #e2e8f0", background: "white",
                  cursor: "pointer", fontSize: 18, color: "#94a3b8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s", flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
 
            {/* Search */}
            <div style={{ position: "relative", marginBottom: 16 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} style={{
                position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
                width: 15, height: 15, pointerEvents: "none",
              }}>
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={inputRef}
                className="ssm-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search characteristics…"
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "9px 12px 9px 34px",
                  borderRadius: 10, border: "1.5px solid #e2e8f0",
                  fontSize: 13, color: "#1e293b",
                  background: "#f8fafc",
                  transition: "all 0.18s",
                  fontFamily: "DM Sans, sans-serif",
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)",
                    width: 18, height: 18, borderRadius: "50%",
                    background: "#94a3b8", border: "none", color: "white",
                    cursor: "pointer", fontSize: 12, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    lineHeight: 1,
                  }}
                >×</button>
              )}
            </div>
          </div>
 
          {/* ── Body ───────────────────────────────────────────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px", scrollbarWidth: "thin", scrollbarColor: "#e2e8f0 transparent" }}>
            {/* Selected preview */}
            <SelectedPreview selected={selected} />
 
            {filtered.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: "#94a3b8", fontSize: 13,
                animation: "fadeSlideIn 0.2s ease",
              }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
                <div style={{ fontWeight: 600, color: "#64748b", marginBottom: 4 }}>No results found</div>
                <div>Try a different search term</div>
              </div>
            ) : (
              filtered.map((svc, i) => (
                <ServiceSection
                  key={svc.service_id}
                  svc={svc}
                  index={i}
                  selected={selected}
                  onSelect={setSelected}
                />
              ))
            )}
          </div>
 
          {/* ── Footer ─────────────────────────────────────────────────── */}
          <div style={{
            padding: "12px 16px 16px",
            borderTop: "1px solid #f1f5f9",
            background: "linear-gradient(0deg, #f8fafc 0%, white 100%)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: 8,
          }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {selected ? `"${selected.char_desc}" ready to add` : "Select a characteristic above"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="ssm-cancel"
                onClick={handleClose}
                style={{
                  padding: "9px 18px",
                  background: "white",
                  border: "1.5px solid #e2e8f0",
                  color: "#64748b",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  transition: "all 0.15s",
                  fontFamily: "DM Sans, sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                className="ssm-add"
                disabled={!selected}
                onClick={handleAdd}
                style={{
                  padding: "9px 22px",
                  background: selected
                    ? "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)"
                    : "#e2e8f0",
                  border: "none",
                  color: selected ? "white" : "#94a3b8",
                  borderRadius: 10,
                  cursor: selected ? "pointer" : "default",
                  fontSize: 13,
                  fontWeight: 700,
                  transition: "all 0.2s",
                  boxShadow: selected ? "0 4px 14px rgba(29,78,216,0.3)" : "none",
                  fontFamily: "DM Sans, sans-serif",
                  letterSpacing: 0.2,
                }}
              >
                + Add Step
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


export default ServiceStepModal;